import { store } from '@restore/store';
import {
  actions as tabCoordinationActions,
  tabCoordinationSelector,
} from '@restore/readerViewerSession/tabCoordination/tabCoordination.slice';
import { formManagementSlice } from '@restore/readerViewerSession/formManagement/formManagement.slice';
import { detachRpcScheme } from './scheme';

const selectTab = () => tabCoordinationSelector(store.getState());

/** Everything the other window needs to show the form exactly as it is here. */
const snapshot = () => {
  const { formDataCache, formIsValid, isDirty, lastSeries } =
    formManagementSlice.selectors.selectFormManagementState(store.getState());

  return {
    formData: formDataCache ?? undefined,
    isValid: formIsValid,
    isDirty,
    lastSeries,
    mainWindowName: selectTab().mainWindowName || window.name,
  };
};

/** Registers the request the detached window sends to the main one. */
export const registerMainDetachHandlers = (): (() => void) =>
  detachRpcScheme.handlers.onInitialize(async () => {
    if (!selectTab().mainTab || !selectTab().isDetached) {
      throw new Error('No detached session is open in the main window');
    }

    // `isDetached` has been true since the detach click, so this
    // window stopped rendering the form long before the detached one booted
    // far enough to ask. Nothing left to take down here.
    return snapshot();
  });

/**
 * Registers what the main window asks of the detached one.
 *
 * The release reply and the attach confirmation share one closure on purpose:
 * confirming an attach must only close a window that actually handed its form
 * back, never one that received a stray event.
 */
export const registerDetachedHandlers = (): (() => void) => {
  let releasedSnapshot: ReturnType<typeof snapshot> | undefined;

  const unsubscribers = [
    detachRpcScheme.handlers.onReleaseForm(async () => {
      if (selectTab().mainTab || !selectTab().mainWindowName) {
        throw new Error('The detached form is not initialized');
      }
      if (releasedSnapshot) return releasedSnapshot;

      releasedSnapshot = snapshot();

      // Deactivating swaps the form for the deactivated screen, which is what
      // unmounts it and lets the bundle destroy its peer. One microtask lets
      // that commit land before main is told the address is free.
      store.dispatch(tabCoordinationActions.setIsDeactivated(true));
      await Promise.resolve();

      return releasedSnapshot;
    }),
    detachRpcScheme.listeners.onAttachCompleted(() => {
      if (!selectTab().mainTab && releasedSnapshot) window.close();
    }),
  ];

  return () => unsubscribers.forEach(unsubscribe => unsubscribe());
};
